/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './LookupReturnValueMapping';

const LookupReturnValueMappingManager: ComponentManagerFn<
  'LookupReturnValueMapping'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'LookupReturnValueMapping'>(
      platform,
      'LookupReturnValueMapping',
    ),
    type: 'LookupReturnValueMapping',
    name: 'Lookup Value Mapping',

    defaultValue,
    isPropertyVisible: (prop) => {
      switch (prop) {
        case 'lookupAttr':
        case 'targetAttribute':
          return true;
        default:
          return false;
      }
    },
    props: {
      lookupAttr: {
        label: 'Lookup Attribute',
        type: 'LookupAttributeChooser',
        category: 'Data',
        required: true,
      },
      targetAttribute: {
        aliasType: 'STORE',
        category: 'Data',
        required: true,
        getAttributeDataTypes: () => 'ALL',
        label: 'Target Attribute',
        preventMultiEdit: true,
        type: 'ViewAttributeChooser',
      },
    },
    getDisplayText: (item) => item.props.targetAttribute,
    category: 'Forms',
    icon: { iconName: 'arrows-h' },
    description: `**Overview**<br/>
The Lookup Value Mapping component is a helper tool designed exclusively for the **Lookup Select** component. It enables automatic data transfer: when a user selects a value from a system Lookup (e.g., a "Currency Code" like "USD"), this component allows you to grab additional metadata associated with that code (like the "Currency Name" or "Symbol") and save it into another field on your form.
Use this component to populate read-only description fields based on a code selection, ensuring your form data is complete and self-explanatory.

**1. Mapping Logic**<br/>
This component bridges the system's static Lookup definition and your dynamic form data.
* **Lookup Attribute:** Selects the specific field from the System Lookup definition to copy.
* **Common Options:** **Meaning** (the display text), **Description** (long text), or other standard lookup columns defined in the system.
* **Target Attribute:** Selects the field in your current form's Data Store where this value will be saved.
* **Alias Type:** **STORE**.

**2. Configuration**<br/>
Like other mapping components, this is a logic-only element with no visual interface.
* **Visibility:** Only the mapping properties (**lookupAttr**, **targetAttribute**) are configurable; all visual and layout settings are hidden as they do not apply to this logic-based component.

**3. Best Use Cases**<br/>
* **Descriptions:** When a user selects a "Department Code" (e.g., 101), automatically fill a read-only field with the "Department Name" (Engineering) so the user confirms they picked the right one.
* **Denormalization:** Storing both the "Status Code" and the "Status Meaning" in your transaction table to make reporting easier later (avoiding the need for complex joins in SQL).
* **Symbol Display:** Selecting a currency "USD" automatically populates a hidden or read-only symbol field with "$".`,
  };
};

export default LookupReturnValueMappingManager;
